import { FC, useMemo, useState, memo } from "react"
import { PaginationState } from "@tanstack/react-table"
import { If, ListSkeleton, Table } from "../../../ui"
import { IntegrationCell } from "./cells"
import BrandAutocomplete from "../../brand-autocomplete"
import { Integration, BBColumnDef, Brand } from "../../../../types"

interface Props {
  todayIntegrations?: Integration[]
  integrations?: Integration[]
  brands?: Brand[]
  selectedBrandId: string
  onSelectedBrandChange: (brandId: string) => void
  isLoading: boolean
  onSelect?: (id: string) => void
  totalCount?: number
  pagination?: PaginationState
  setPagination?: React.Dispatch<React.SetStateAction<PaginationState>>
}

type ChipFilter =
  | "All"
  | "Lead Workflow"
  | "Rate Table"
  | "Rate Cards"
  | "LO page"
  | "Calculators"
  | "Reviews"

const CHIPS: ChipFilter[] = [
  "All",
  "Lead Workflow",
  "Rate Table",
  "Rate Cards",
  "LO page",
  "Calculators",
  "Reviews"
]

function matchesChip(integration: Integration, chip: ChipFilter): boolean {
  if (chip === "All") return true
  const { type, app_settings } = integration
  switch (chip) {
    case "LO page":
      return type === "boomtown"
    case "Lead Workflow":
      return !!app_settings?.lead_workflow
    case "Rate Table":
      return !!app_settings?.rate_table_config
    case "Rate Cards":
      return !!app_settings?.rateCardsConfigId
    case "Calculators":
      return !!(app_settings?.mort_calc || app_settings?.afford_calc)
    case "Reviews":
      return !!(
        app_settings?.settings?.loReviews ||
        app_settings?.settings?.brandReviews
      )
    default: {
      const _exhaustive: never = chip
      throw new Error(`Unhandled ChipFilter: ${_exhaustive}`)
    }
  }
}

function timestampToDate(created: string): Date {
  return new Date(parseInt(created, 10) * 1000)
}

function formatDateHeader(created: string): string {
  const date = timestampToDate(created)
  const today = new Date()
  if (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  ) {
    return "TODAY"
  }
  return date
    .toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric"
    })
    .toUpperCase()
}

function groupByDate(
  integrations: Integration[]
): { label: string; items: Integration[] }[] {
  const groups: Map<string, Integration[]> = new Map()

  const sorted = [...integrations].sort((a, b) => {
    const aTs = a.created ? parseInt(a.created, 10) : 0
    const bTs = b.created ? parseInt(b.created, 10) : 0
    return bTs - aTs
  })

  for (const integration of sorted) {
    const dateKey = integration.created
      ? timestampToDate(integration.created).toDateString()
      : "Unknown"
    if (!groups.has(dateKey)) groups.set(dateKey, [])
    groups.get(dateKey)!.push(integration)
  }

  return Array.from(groups.entries()).map(([, items]) => ({
    label: items[0].created
      ? formatDateHeader(items[0].created)
      : "UNKNOWN DATE",
    items
  }))
}

const IntegrationsTable: FC<Props> = memo(
  ({
    todayIntegrations = [],
    integrations = [],
    brands = [],
    selectedBrandId,
    onSelectedBrandChange,
    isLoading,
    onSelect,
    totalCount = 0,
    pagination,
    setPagination
  }) => {
    const [activeChip, setActiveChip] = useState<ChipFilter>("All")

    const pageCount = useMemo(() => {
      if (!pagination || !totalCount) return -1
      return Math.ceil(totalCount / pagination.pageSize)
    }, [totalCount, pagination])

    const filteredToday = useMemo(
      () => todayIntegrations.filter(i => matchesChip(i, activeChip)),
      [todayIntegrations, activeChip]
    )

    const filteredHistory = useMemo(
      () => integrations.filter(i => matchesChip(i, activeChip)),
      [integrations, activeChip]
    )

    const historyGroups = useMemo(
      () => groupByDate(filteredHistory),
      [filteredHistory]
    )

    const columns: BBColumnDef<Integration>[] = useMemo(
      () => [
        {
          id: "title",
          header: () => <>Name</>,
          footer: props => props.column.id,
          cell: info => (
            <IntegrationCell data={info.row.original} onSelect={onSelect} />
          )
        }
      ],
      [onSelect]
    )

    return (
      <div className="mt-6">
        <If
          condition={isLoading}
          then={<ListSkeleton />}
          else={
            <>
              {/* Toolbar */}
              <div className="flex items-center justify-between gap-4 flex-wrap">
                {/* TODO: re-enable once backend supports feature-tag filtering */}
                {false && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {CHIPS.map(chip => (
                      <button
                        key={chip}
                        type="button"
                        onClick={() => setActiveChip(chip)}
                        className={[
                          "rounded-full px-4 py-1.5 text-sm border transition-colors",
                          activeChip === chip
                            ? "bg-[#f4effe] border-[#4a1a9d] text-[#4a1a9d] font-medium"
                            : "bg-white border-[#e4e4e4] text-[#232227] font-normal"
                        ].join(" ")}
                      >
                        {chip}
                      </button>
                    ))}
                  </div>
                )}
                {brands.length > 0 && (
                  <BrandAutocomplete
                    hideLabel
                    brands={brands}
                    selected={selectedBrandId}
                    onChange={onSelectedBrandChange}
                  />
                )}
              </div>

              {/* Date-grouped rows */}
              <div className="mt-8">
                {/* TODAY — always pinned at top */}
                {filteredToday.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-2 mt-4">
                      <span className="text-xs font-semibold uppercase tracking-widest text-[#757575] whitespace-nowrap">
                        TODAY
                      </span>
                      <div className="flex-1 h-px bg-[#e4e4e4]" />
                    </div>
                    <Table
                      data={filteredToday}
                      columns={columns}
                      viewType="table"
                      pageSize={Math.max(filteredToday.length, 1)}
                      pageCount={1}
                      searchBarVisible={false}
                      noHeader
                      removeMobilePagination
                    />
                  </div>
                )}

                {/* Historical — server-paginated, grouped by date */}
                {historyGroups.map(group => (
                  <div key={group.label}>
                    <div className="flex items-center gap-3 mb-2 mt-4">
                      <span className="text-xs font-semibold uppercase tracking-widest text-[#757575] whitespace-nowrap">
                        {group.label}
                      </span>
                      <div className="flex-1 h-px bg-[#e4e4e4]" />
                    </div>
                    <Table
                      data={group.items}
                      columns={columns}
                      viewType="table"
                      pageSize={Math.max(group.items.length, 1)}
                      pageCount={1}
                      searchBarVisible={false}
                      noHeader
                      removeMobilePagination
                    />
                  </div>
                ))}

                {/* Pagination for historical results */}
                {pageCount > 1 && (
                  <Table
                    data={[]}
                    columns={columns}
                    viewType="table"
                    pageSize={pagination?.pageSize || 10}
                    pageCount={pageCount}
                    pagination={pagination}
                    setPagination={setPagination}
                    rowCount={totalCount}
                    searchBarVisible={false}
                    noHeader
                    removeMobilePagination
                    noDataElement={<></>}
                  />
                )}
              </div>
            </>
          }
        />
      </div>
    )
  }
)

IntegrationsTable.displayName = "IntegrationsTable"

export default IntegrationsTable
